import {
  Preflight,
  StyleSheetManager,
  ThemeProvider
} from '@xstyled/styled-components';
import React, { useEffect, useState } from 'react';
import {
  isConsole,
  isEmbedded,
  isIE,
  isMobile,
  isSamsungBrowser,
  isSmartTV
} from 'react-device-detect';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { select } from '~/lib/select';
import { TrainingState, useTrainingStore } from '~/training/state';
import { useLoadTranslation } from '~/lib/translations';
import { Training } from '~/training/Training';
import { NotFound } from '~/ui/not-found/NotFound';
import { OldBrowsers } from '~/ui/not-found/OldBrowsers';
import { Toasts } from '~/ui/toast/Toasts';
import { Typography } from '~/ui/typography/Typography';
import { MainStyle } from './MainStyle';
import { appTheme } from './theme';
import { DarkStyle } from './DarkStyle';
import { Editor } from '~/editor/Editor';
import { TranslationsOnlyRouteGuard } from '~/editor/translation/TranslationsOnlyRouteGuard';
import { useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';
import { Spinner } from '~/ui/spinner/Spinner';
import { EditorProvider } from '~/shell/Store';
import { isForceDarkTheme, isForceLightTheme } from '~/lib/themes/forceTheme';
import { Preview } from '~/preview/Preview';
import { refreshBrandContrastForSurface } from '~/lib/themes/applyBrandColors';

const selector = (s: TrainingState) => select(s, 'darkMode', 'textLocale');

const resolveSurfaceIsDark = (darkMode: string | null | undefined) => {
  if (
    window?.location?.pathname === '/editor' ||
    window?.location?.pathname === '/preview'
  ) {
    return false;
  }
  if (isForceDarkTheme()) return true;
  if (isForceLightTheme()) return false;
  if (
    darkMode === 'automatic' &&
    window?.matchMedia?.('(prefers-color-scheme: dark)')?.matches
  ) {
    return true;
  }
  return darkMode === 'dark';
};

const HandleNewRoute = () => {
  const location = useLocation();
  const history = useHistory();
  const { search, pathname } = location;
  const [loader, setLoader] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(search);
    if (params.has('token')) {
      const token = params.get('token') || '';

      params.delete('token');
      const newSearch = params.toString();
      const newPath = newSearch ? `${pathname}?${newSearch}` : pathname;

      sessionStorage.setItem('token', token);
      history.replace(newPath);
    } else {
      setLoader(false);
    }
  }, [location, history, pathname, search]);

  return loader ? <Spinner /> : <Training />;
};

export const App = () => {
  useLoadTranslation();
  const { darkMode, textLocale } = useTrainingStore(selector);

  let Theme;

  if (
    window?.location?.pathname === '/editor' ||
    window?.location?.pathname === '/preview'
  ) {
    Theme = MainStyle;
  } else if (isForceDarkTheme()) {
    Theme = DarkStyle;
  } else if (isForceLightTheme()) {
    Theme = MainStyle;
  } else if (
    darkMode === 'automatic' &&
    window?.matchMedia?.('(prefers-color-scheme: dark)')?.matches
  ) {
    Theme = DarkStyle;
  } else {
    Theme = darkMode === 'dark' ? DarkStyle : MainStyle;
  }

  const surfaceIsDark = resolveSurfaceIsDark(darkMode);

  // CAT-1261: focus / on-accent tokens depend on page surface (light vs dark).
  useEffect(() => {
    refreshBrandContrastForSurface(surfaceIsDark);
  }, [surfaceIsDark]);

  return (
    <StyleSheetManager disableVendorPrefixes>
      <ThemeProvider theme={appTheme}>
        <Helmet htmlAttributes={{ lang: textLocale }}>
          {isMobile && (
            <meta
              name="viewport"
              content="width=device-width, initial-scale=1, maximum-scale=1"
            />
          )}
        </Helmet>
        <Preflight />
        <Theme />
        <Typography />
        <Toasts />
        <BrowserRouter>
          <TranslationsOnlyRouteGuard />
          <Switch>
            <Route path="/editor">
              <EditorProvider editorMode={true}>
                <Editor />
              </EditorProvider>
            </Route>
            <Route path="/preview">
              <Preview />
            </Route>
            <Route path="/l/e/t/:learnerTrainingId">
              {isIE ||
              isConsole ||
              isEmbedded ||
              isSmartTV ||
              isSamsungBrowser ? (
                <OldBrowsers />
              ) : (
                <HandleNewRoute />
              )}
            </Route>
            <Route path="/l/:learnerId/t/:learnerTrainingId">
              {isIE ||
              isConsole ||
              isEmbedded ||
              isSmartTV ||
              isSamsungBrowser ? (
                <OldBrowsers />
              ) : (
                <EditorProvider editorMode={false}>
                  <Training />
                </EditorProvider>
              )}
            </Route>
            <Route path="*">
              <NotFound />
            </Route>
          </Switch>
        </BrowserRouter>
      </ThemeProvider>
    </StyleSheetManager>
  );
};
